import * as React from 'react'

import { Button, Input } from '@thg-commerce/gravity-elements'
import { styled } from '@thg-commerce/gravity-patterns/theme'
import { margin, mq, padding, spacing, Text } from '@thg-commerce/gravity-theme'
import { HorizontalAlignment } from '@thg-commerce/gravity-theme/alignments'

export interface StoreSearchProps {
  i18nText: {
    title: string
    subtitle: string
    inputLabel: string
    buttonLabel: string
    infoLabel?: string
    infoLinkLabel?: string
  }
  alignment: HorizontalAlignment
  searchInputOverride?: string
  selectedStoreLocation?: {
    latitude: number
    longitude: number
  }
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFocus: (event: React.FocusEvent) => void
  clearInputValueHandler: (event: React.SyntheticEvent) => void
  closeDropdown: () => void
  autocompletePredictions?: JSX.Element | null
  showInfoLabel?: boolean
  searchStoreHandler: (value: string) => void
}

const StoreSearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background-color: ${(props) =>
    props.theme.patterns.storeSearch.backgroundColor};

  ${(props) => padding(props.theme.patterns.storeSearch.padding)};
`

const StoreSearchTitle = styled.h1<{ alignment: HorizontalAlignment }>`
  ${(props) =>
    Text(
      props.theme.patterns.storeSearch.title.font.entry,
      props.theme.patterns.storeSearch.title.font.style,
    )};

  width: 100%;
  max-width: 310px;
  text-align: center;
  color: ${(props) => props.theme.patterns.storeSearch.title.font.textColor};
  text-decoration: ${(props) =>
    props.theme.patterns.storeSearch.title.font.textDecoration};

  ${(props) => margin(props.theme.patterns.storeSearch.title.margin)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    max-width: 416px;
    text-align: ${(props) => props.alignment};
    margin-right: auto;
    margin-left: ${(props) =>
      props.alignment === HorizontalAlignment.CENTER ? 'auto' : '0'};
  }
`

const StoreSearchSubtitle = styled.p<{ alignment: HorizontalAlignment }>`
  ${(props) =>
    Text(
      props.theme.patterns.storeSearch.subtitle.font.entry,
      props.theme.patterns.storeSearch.subtitle.font.style,
    )};

  width: 100%;
  text-align: center;
  color: ${(props) => props.theme.patterns.storeSearch.subtitle.font.textColor};
  text-transform: ${(props) =>
    props.theme.patterns.storeSearch.subtitle.font.transform};
  text-decoration: ${(props) =>
    props.theme.patterns.storeSearch.subtitle.font.textDecoration};

  ${(props) => margin(props.theme.patterns.storeSearch.subtitle.margin)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    text-align: ${(props) => props.alignment};
  }
`

const SearchForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: center;
`

const InputWrapper = styled.div<{ alignment: HorizontalAlignment }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  width: 100%;
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    width: ${(props) =>
      props.alignment === HorizontalAlignment.CENTER
        ? props.theme.patterns.storeSearch.searchInput.width
        : `100%`};
  }

  ${(props) => margin(props.theme.patterns.storeSearch.searchInput.margin)};

  > div {
    width: 100%;
    ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
      width: ${(props) =>
        props.alignment === HorizontalAlignment.CENTER
          ? props.theme.patterns.storeSearch.searchInput.width
          : `100%`};
    }
  }
`

const SearchButton = styled(Button)`
  height: 48px;
  width: 120px;
`

const StyledInput = styled(Input)`
  border-radius: ${(props) =>
    props.theme.patterns.storeSearch.searchInput.borderRadius};
`

const InfoLabel = styled.span`
  width: 100%;
  margin-top: ${spacing(2)};
  ${Text('bodyText', 'default')};
  text-align: left;
`

const StyledLinkText = styled(Button)`
  ${Text('bodyText', 'alternate')};
  text-decoration: underline;
  height: 48px;
`

export const StoreSearch = (props: StoreSearchProps) => {
  const inputRef = React.useRef(null)
  const autocompleteRef = React.useRef(null)
  const [inputValue, setInputValue] = React.useState<string>(
    props.searchInputOverride || '',
  )

  const handleClickOutside = (event) => {
    if (
      inputRef.current &&
      inputRef.current !== event.target &&
      autocompleteRef.current &&
      autocompleteRef.current !== event.target
    ) {
      props.closeDropdown()
    }
  }

  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  const handleSearchSubmit = () => {
    props.searchStoreHandler && props.searchStoreHandler(inputValue)
  }

  return (
    <StoreSearchWrapper>
      <StoreSearchTitle alignment={props.alignment}>
        {props.i18nText.title}
      </StoreSearchTitle>
      <StoreSearchSubtitle alignment={props.alignment}>
        {props.i18nText.subtitle}
      </StoreSearchSubtitle>
      <SearchForm
        onSubmit={(event) => {
          event.preventDefault()
          handleSearchSubmit()
        }}
      >
        <InputWrapper alignment={props.alignment}>
          <StyledInput
            valueOverride={props.searchInputOverride || ''}
            label={props.i18nText.inputLabel}
            required={false}
            type="text"
            onChange={(event) => {
              props.onChange(event)
              setInputValue(event.target.value)
            }}
            onFocus={props.onFocus}
            clearInputValueHandler={props.clearInputValueHandler}
            bindref={inputRef}
          />
          <div ref={autocompleteRef}>{props.autocompletePredictions}</div>
        </InputWrapper>
        <SearchButton type="submit" emphasis="high">
          {props.i18nText.buttonLabel}
        </SearchButton>
      </SearchForm>
      {props.showInfoLabel &&
        props.i18nText.infoLabel &&
        props.i18nText.infoLinkLabel && (
          <InfoLabel>
            {props.i18nText.infoLabel}
            {` `}
            <StyledLinkText emphasis="low" renderedAs="a" href="/stores/uk">
              {props.i18nText.infoLinkLabel}
            </StyledLinkText>
          </InfoLabel>
        )}
    </StoreSearchWrapper>
  )
}
